import { useState } from "react";
import { useCatalog, splitParagraphs } from "../lib/products";
import { useOpenFromUrl } from "../lib/openFromUrl";
import {
  FileDown,
  ExternalLink,
  Check,
  ArrowRight,
  PlayCircle,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Reveal } from "./Reveal";
import { YouTubePlayer } from "./VideoGallery";
import { AttachmentsBlock } from "./Allegati";
import { InstallatiDaNoi } from "./InstallatiDaNoi";
import { scrollToSection } from "../lib/scroll";
import { CHRIST_PRODUCTS } from "../data/christProducts";
import { videoForProduct } from "../data/christVideos";

const ProductCard = ({ p, onOpen, delay }) => (
  <Reveal delay={delay}>
    <article
      data-testid={`christ-sheet-card-${p.key}`}
      onClick={() => onOpen(p)}
      className="group h-full cursor-pointer overflow-hidden rounded-2xl border border-slate-200 bg-white transition-[box-shadow,transform,border-color] duration-500 hover:-translate-y-1.5 hover:border-[#E30613]/30 hover:shadow-[0_24px_48px_-12px_rgba(11,31,58,0.18)]"
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
        <img
          src={p.img}
          alt={`Christ ${p.name}`}
          loading="lazy"
          className="h-full w-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
        />
        <span className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-[#0B1F3A] backdrop-blur">
          {p.type}
        </span>
        {p.pdf && (
          <span className="absolute right-4 top-4 inline-flex items-center gap-1 rounded-full bg-[#E30613] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-white">
            <FileDown className="h-3 w-3" /> PDF
          </span>
        )}
      </div>
      <div className="p-6">
        <h3 className="font-display text-xl font-extrabold tracking-tight text-[#0B1F3A]">
          {p.name}
        </h3>
        <p className="mt-2 text-left text-sm leading-relaxed text-[#4F5D70]" data-testid={`christ-sheet-short-${p.key}`}>
          {p.short}
        </p>
        <div className="mt-4 flex items-center gap-2 text-sm font-bold text-[#0B1F3A]">
          Apri scheda
          <ArrowRight className="h-4 w-4 text-[#E30613] transition-transform duration-300 group-hover:translate-x-1" />
        </div>
      </div>
    </article>
  </Reveal>
);

const ProductSheet = ({ p, onClose }) => {
  const [showVideo, setShowVideo] = useState(false);
  const video = p ? videoForProduct(p.key) : null;
  const close = () => {
    setShowVideo(false);
    onClose();
  };
  return (
    <Dialog open={!!p} onOpenChange={close}>
      <DialogContent
            data-lenis-prevent
        className="max-h-[90vh] max-w-5xl overflow-y-auto rounded-3xl border-0 p-0"
        data-testid="christ-sheet-modal"
      >
        {p && (
          <div className="grid md:grid-cols-[5fr_6fr]">
            {showVideo && video ? (
              <div className="p-4 md:flex md:items-center">
                <YouTubePlayer
                  video={video}
                  brand="Christ Wash Systems"
                  autoplay
                  className="rounded-2xl"
                />
              </div>
            ) : (
              <div className="bg-[#F2F5F9] md:h-full">
                <img
                  src={p.img}
                  alt={`Christ ${p.name}`}
                  className="aspect-[16/10] w-full object-cover object-center md:aspect-[4/3]"
                />
                <p className="hidden px-6 py-5 text-left text-xs leading-relaxed text-[#64748B] md:block">
                  Immagine ufficiale Otto Christ AG. L'impianto viene configurato su misura in base al sito e ai programmi scelti.
                </p>
              </div>
            )}
            <div className="p-7 text-left sm:p-9">
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#E30613]">
                Christ Wash Systems · {p.type}
              </span>
              <DialogTitle className="font-display mt-2 text-left text-3xl font-extrabold tracking-tight text-[#0B1F3A]">
                {p.name}
              </DialogTitle>
              <DialogDescription className="mt-3 text-left text-sm leading-relaxed text-[#475569]">
                {p.short}
              </DialogDescription>
              <div className="mt-4 space-y-3 text-left" data-testid="christ-sheet-description">
                {p.description.map((par) => (
                  <p key={par.slice(0, 40)} className="text-sm leading-relaxed text-[#334155]">
                    {par}
                  </p>
                ))}
              </div>
              {p.target && (
                <div className="mt-4 rounded-xl bg-slate-50 px-4 py-3 text-left text-xs text-[#4F5D70]">
                  <span className="font-bold uppercase tracking-wider text-[#0B1F3A]">
                    Ideale per:
                  </span>{" "}
                  {p.target}
                </div>
              )}
              {p.features.length > 0 && <h4 className="mt-5 text-xs font-bold uppercase tracking-wider text-[#0B1F3A]">Punti di forza</h4>}
              <ul
                className="mt-2 space-y-2.5"
                data-testid="christ-sheet-features"
              >
                {p.features.map((f) => (
                  <li
                    key={f}
                    className="flex items-start gap-2.5 text-sm text-[#0B1F3A]"
                  >
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#E30613]" />
                    {f}
                  </li>
                ))}
              </ul>
              {(p.versions || []).length > 0 && <h4 className="mt-5 text-xs font-bold uppercase tracking-wider text-[#0B1F3A]">Versioni e dotazioni disponibili</h4>}
              <ul className="mt-2 flex flex-wrap gap-2" data-testid="christ-sheet-versions">
                {(p.versions || []).map((v) => (
                  <li key={v} className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-[#0B1F3A]">
                    {v}
                  </li>
                ))}
              </ul>
              <AttachmentsBlock productKey={p.key} />
              <InstallatiDaNoi productKey={p.key} />
              <div className="mt-7 flex flex-wrap gap-3">
                <button
                  data-testid="christ-sheet-quote-button"
                  onClick={() => {
                    close();
                    scrollToSection("contatti");
                  }}
                  className="inline-flex items-center gap-2 rounded-full bg-[#E30613] px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#C20410]"
                >
                  Richiedi preventivo
                  <ArrowRight className="h-4 w-4" />
                </button>
                {p.pdf && (
                  <a
                    data-testid="christ-sheet-pdf-link"
                    href={p.pdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded-full border border-[#0B1F3A] px-5 py-2.5 text-sm font-bold text-[#0B1F3A] transition-colors hover:bg-[#0B1F3A] hover:text-white"
                  >
                    <FileDown className="h-4 w-4" />
                    {p.pdfLabel}
                  </a>
                )}
                {video && (
                  <button
                    type="button"
                    data-testid="christ-sheet-video-button"
                    onClick={() => setShowVideo((s) => !s)}
                    className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-5 py-2.5 text-sm font-bold text-[#0B1F3A] transition-colors hover:border-[#E30613] hover:text-[#E30613]"
                  >
                    <PlayCircle className="h-4 w-4" />
                    {showVideo ? "Nascondi video" : "Guarda il video"}
                  </button>
                )}
                {p.page && <a
                  data-testid="christ-sheet-official-link"
                  href={p.page}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold text-[#4F5D70] transition-colors hover:text-[#0B1F3A]"
                >
                  <ExternalLink className="h-4 w-4" />
                  Scheda ufficiale Christ
                </a>}
              </div>
              <p className="mt-5 text-[11px] text-[#94A3B8]">
                Immagini e dati tecnici: Otto Christ AG. Specifiche soggette a
                configurazione dell'impianto.
              </p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const customChrist = (d) => ({
  key: d.key,
  name: d.name,
  type: d.sub || "Impianto di lavaggio",
  target: "",
  img: d.img || "/christ/alpha.jpg",
  short: d.short,
  summary: d.short,
  description: splitParagraphs(d.description),
  features: [],
  versions: [],
  page: "",
  custom: true,
});

export const SchedeChrist = () => {
  const [open, setOpen] = useState(null);
  const products = useCatalog("christ", CHRIST_PRODUCTS, "short", customChrist);
  useOpenFromUrl(products, setOpen);
  return (
    <section
      id="schede-christ"
      data-testid="schede-christ-section"
      className="bg-white py-20 lg:py-28"
    >
      <div className="mx-auto max-w-[1440px] px-4 sm:px-8 lg:px-16">
        <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
          <div className="max-w-2xl">
            <Reveal>
              <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#E30613]">
                Schede prodotto Christ
              </span>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-display mt-4 text-3xl font-extrabold tracking-tight text-[#0B1F3A] sm:text-4xl">
                La gamma completa, impianto per impianto.
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <p className="max-w-md text-sm leading-relaxed text-[#4F5D70] sm:text-base">
              Portali, tunnel, self-service e lavaggio mezzi pesanti: apri la
              scheda per caratteristiche, brochure PDF e link alla pagina
              ufficiale.
            </p>
          </Reveal>
        </div>

        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {products.map((p, i) => (
            <ProductCard key={p.key} p={p} onOpen={setOpen} delay={0.04 * i} />
          ))}
        </div>
      </div>
      <ProductSheet p={open} onClose={() => setOpen(null)} />
    </section>
  );
};
